import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useInviteEmployee, usePortalEmployees } from '@/hooks/use-portal'
import type { PortalEmployee } from '@/services/portal.service'
import { Loader2, Mail, Search, Users } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'

export function PortalEmployeesPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [confirmEmployee, setConfirmEmployee] = useState<PortalEmployee | null>(null)

  const { data, isLoading } = usePortalEmployees({ page, limit: 50, ...(search ? { search } : {}) })
  const inviteMutation = useInviteEmployee()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleInvite = async () => {
    if (!confirmEmployee) return
    try {
      await inviteMutation.mutateAsync(confirmEmployee.id)
      setConfirmEmployee(null)
    } catch (error) {
      console.error('Failed to send invite:', error)
    }
  }

  const getStatusBadge = (emp: PortalEmployee) => {
    if (emp.onboarding_status === 'invited') {
      return (
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-green-100 text-green-700">
          Invited
        </span>
      )
    }
    if (emp.onboarding_status === 'pending') {
      return (
        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700">
          Pending
        </span>
      )
    }
    return (
      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-500">
        Not Invited
      </span>
    )
  }

  return (
    <div className="p-8 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">People</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.total ?? 0} people in your organization
          </p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : data?.employees.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No people found</p>
            <p className="text-sm text-muted-foreground">
              {search ? 'Try a different search term' : 'Import people to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <div className="rounded-lg border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="h-10 px-4 text-left text-sm font-medium">Name</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Email</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Phone</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Location</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Seniority</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Invite Status</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Added</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.employees.map((emp) => (
                      <tr key={emp.id} className="border-b">
                        <td className="p-4 text-sm font-medium">
                          {[emp.first_name, emp.last_name].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{emp.email || '-'}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {emp.phone_number || '-'}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{emp.location || '-'}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {emp.seniority || '-'}
                        </td>
                        <td className="p-4">{getStatusBadge(emp)}</td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {new Date(emp.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          {emp.onboarding_status !== 'invited' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmEmployee(emp)}
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              Send Welcome
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {data.page} of {data.pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= (data?.pages ?? 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      <Dialog open={!!confirmEmployee} onOpenChange={(open) => !open && setConfirmEmployee(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Welcome Message</DialogTitle>
            <DialogDescription>
              This will send a welcome email to{' '}
              <strong>
                {confirmEmployee
                  ? [confirmEmployee.first_name, confirmEmployee.last_name]
                      .filter(Boolean)
                      .join(' ')
                  : ''}
              </strong>{' '}
              at <strong>{confirmEmployee?.email}</strong> inviting them to Pelgo.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
            <p className="text-sm text-amber-800 font-medium">This action cannot be undone.</p>
            <p className="text-xs text-amber-700 mt-1">
              They will receive a welcome message and be able to log into Pelgo.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmEmployee(null)}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Welcome
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
